import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';
import { CONTENT_METADATA_URL, SAVE_CONTENT_METADATA_URL } from '../../utils/Constants';
import { formatDateToLastUpdated, getJwtToken, langSwitch } from '../utils';
import { getUpdateFieldsErrMsg } from '../../utils/utils';
import { save_questions } from './questions';
import { save_chapters } from './chapters';
import { COURSES_METADATA_URL } from '../../utils/Constants';

/* How to inject the authorization header once for all request? */
axios.interceptors.request.use(
  async function (config) {
    const jwtToken = await getJwtToken();
    config.headers = { Authorization: jwtToken };
    config.params = { filterFields: false };
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

export const subscriptionsList = [
  {
    displayName: 'BMJ Learning standard',
    id: '1',
    code: 'bmj-learning-standard'
  },
  {
    displayName: 'Free',
    id: '2',
    code: 'bmj-learning-free'
  }
];

const emptyCourse = () => ({
  objectives: [],
  title: '',
  dateUpdated: formatDateToLastUpdated(),
  accessResources: [],
  duration: {},
  acknowledgements: '',
  contributors: [],
  overview: [],
  doi: '',
  targetAudiences: [],
  homepageImage: '',
  category: [],
  partnersLogo: [],
  partnerStatement: '',
  description: '',
  facultyDisclosure: '',
  updateTitle: '',
  inProgress: false,
  locale: 'en_GB',
  type: 'COURSE',
  moduleType: 'EL2',
  status: 'PEND',
  videoId: ''
});

const initialState = {
  data: emptyCourse(),
  status: 'idle',
  err: false,
  serverError: false,
  errMsg: '',
  subscriptionsList,
  coursesIncluded: [],
  relatedVidCourses: [],
  additionalSubscriptions: []
};

export const courseSlice = createSlice({
  name: 'course',
  initialState,
  reducers: {
    SET_COURSE(state, { payload }) {
      state.data = { ...emptyCourse(), ...payload };
      const courseDescription = payload.updateDescription?payload.updateDescription:payload.facultyDisclosure
      state.data.updateDescription=courseDescription
    },
    HANDLE_NEW_COURSE(state) {
      state.data = emptyCourse();
      state.data.contributors = [];
      state.coursesIncluded = [];
      state.relatedVidCourses = [];
    },
    SET_RELATED_VID_COURSES(state, { payload }) {
      state.relatedVidCourses = payload;
    },
    UPDATE_COURSE(state, { payload: { key, value } }) {
      state.data[key] = value;
    },
    HANDLE_STATUS(state, { payload }) {
      state.status = payload;
    },
    SET_COURSE_PROP(state, { payload: { key, value } }) {
      state.data[key] = value;
    },
    UPDATE_LAST_COURSE_METADATA(state, { payload }) {
      state.coursesIncluded[state.coursesIncluded.length - 1] = payload;
    },
    HANDLE_COURSE_STATUS(state, { payload: { key, value } }) {
      state.coursesIncluded[state.coursesIncluded.length - 1][key] = value;
    },
    UPDATE_SUBSCRIPTIONS(state, { payload: item }) {
      const subscriptionCopy = [...state.data.accessResources];
      const index = subscriptionCopy.findIndex((obj) => +obj.id === +item.id);
      if (index === -1) {
        const i = subscriptionsList.findIndex((o) => +o.id === +item.id);
        subscriptionCopy.push(subscriptionsList[i]);
      } else {
        subscriptionCopy.splice(index, 1);
      }
      state.data.accessResources = subscriptionCopy;
    },
    UPDATE_ADDITIONAL_SUBSCRIPTION(state, { payload = [] }) {
      const array = state.data.accessResources.filter((obj) => +obj.id < 3);
      payload.forEach((itemInPayload) => {
        array.push(itemInPayload);
      });
      state.data.accessResources = array;
    },
    HANDLE_CHIP_SELECTION(state, { payload: { section, id, action, list } }) {
      let chipIndex;
      switch (action) {
        case 'selectAll':
          state.data[section] = [...list];
          break;
        case 'deselectAll':
          state.data[section] = [];
          break;
        case 'single':
          chipIndex = state.data[section].findIndex((obj) => +obj.id === +id);
          if (chipIndex === -1) {
            const index = list.findIndex((obj) => +obj.id === +id);
            const chip = list[index];
            state.data[section].push(chip);
          } else {
            state.data[section].splice(chipIndex, 1);
          }
          break;
        default:
          break;
      }
    },
    HANDLE_AUTHOR(state, { payload: { key, value, index } }) {
      const authors = [...state.data.contributors];
      authors[index][key] = value;
      state.data.contributors = authors;
    },
    HANDLE_ADD_DELETE_AUTHOR(state, { payload }) {
      let data;
      if (payload === 'add') {
        const newAuthor = {
          name: '',
          type: 'original-author',
          description: '',
          disclosure: ''
        };
        data = [...state.data.contributors, newAuthor];
        state.data.contributors = data;
      } else {
        data = [...state.data.contributors];
        data.pop();
        state.data.contributors = data;
      }
      UPDATE_COURSE(state, { payload: { key: 'contributors', value: state.data.contributors } });
    },
    HANDLE_ADD_DELETE_OVERVIEW_SECTION(state, { payload }) {
      let data;
      if (payload === 'add') {
        const newSection = {
          title: '',
          description: ''
        };
        data = [...state.data.overview, newSection];
        state.data.overview = data;
      } else {
        data = [...state.data.overview];
        data.pop();
        state.data.overview = data;
      }
      UPDATE_COURSE(state, { payload: { key: 'overview', value: state.data.overview } });
    },
    SET_ERR_MSG(state, { payload }) {
      state.errMsg = payload;
    },
    HANDLE_SERVER_ERR(state, { payload }) {
      state.serverError = payload;
    },
    ADD_COURSE(state) {
      state.coursesIncluded.push({});
    },
    HANDLE_OVERVIEW_SECTION(state, { payload: { key, value, index } }) {
      const overview = [...state.data.overview];
      overview[index][key] = value;
      state.data.overview = overview;
    }
  }
});

export const {
  SET_COURSE,
  HANDLE_NEW_COURSE,
  UPDATE_COURSE,
  UPDATE_SUBSCRIPTIONS,
  UPDATE_ADDITIONAL_SUBSCRIPTION,
  HANDLE_CHIP_SELECTION,
  HANDLE_AUTHOR,
  HANDLE_ADD_DELETE_AUTHOR,
  HANDLE_STATUS,
  SET_ERR_MSG,
  HANDLE_SERVER_ERR,
  UPDATE_LAST_COURSE_METADATA,
  ADD_COURSE,
  HANDLE_COURSE_STATUS,
  HANDLE_OVERVIEW_SECTION,
  HANDLE_ADD_DELETE_OVERVIEW_SECTION,
  SET_RELATED_VID_COURSES
} = courseSlice.actions;

export const get_course = ({ id, language }) =>
  async function (dispatch) {
    try {
      dispatch(HANDLE_STATUS('loading'));
      dispatch(SET_RELATED_VID_COURSES([]));
      const url = CONTENT_METADATA_URL.replace('{id}', id).replace('{language}', language);
      const { data } = await axios.get(url);
      // if we don't receive an array of authors, we need to add an empty array
      // to avoid data type errors
      !data.contributors && (data.contributors = []);
      !data.overview && (data.overview = []);
      const parsedData = JSON.stringify(data);
      dispatch(SET_COURSE(data));
      dispatch(HANDLE_STATUS('idle'));
      if (data.relatedCourseIds && data.relatedCourseIds.length) {
        if (data.moduleType === 'VID') {
          const url1 = `${COURSES_METADATA_URL}?moduleIds=${data.relatedCourseIds[0]}&language=${langSwitch(language)}`;
          const { data: relatedCourseData } = await axios.get(url1);
          dispatch(UPDATE_LAST_COURSE_METADATA(relatedCourseData.courses[0]));
        } else {
          dispatch(SET_RELATED_VID_COURSES(data.relatedCourseIds.map((courseId) => ({ moduleId: courseId }))));
          const relatedCourseFetches = data.relatedCourseIds.map((courseId) => {
            const relUrl = CONTENT_METADATA_URL.replace('{id}', courseId).replace('{language}', language);
            return axios.get(relUrl).then(({ data: relData }) => ({ moduleId: String(relData.id), title: relData.title })).catch(() => null);
          });
          const relatedCourses = await Promise.all(relatedCourseFetches);
          const resolved = relatedCourses.filter(Boolean);
          if (resolved.length > 0) {
            dispatch(SET_RELATED_VID_COURSES(resolved));
          }
        }
      }
      return JSON.parse(parsedData);
    } catch (e) {
      dispatch(HANDLE_STATUS('error'));
    }
  };

export const save_course = () =>
  async function (dispatch, getState) {
    try {
      const {
        course: { data, coursesIncluded }
      } = getState();
      const course = { ...data };
      (!course.partnersLogo || !course.partnersLogo.length) && delete course.partnersLogo;
      (!course.partnerStatement || !course.partnerStatement.length) && delete course.partnerStatement;
      delete course.isRtop;
      delete course.isNew;
      delete course.isUpdated
      // course.accessResources = setAccessResources(course, additionalSubscriptions);
      const saveErrMsg = getUpdateFieldsErrMsg(course.updateTitle, course.updateDescription, course.inProgress);
      if (saveErrMsg) {
        console.error('[CourseEdit] Validation error:', saveErrMsg);
        dispatch(SET_ERR_MSG(saveErrMsg));
        dispatch(HANDLE_STATUS('error'));
        dispatch(HANDLE_SERVER_ERR(true));
      }
      else {
      if (course.moduleType !== 'VID') {
        delete course.overview;
        delete course.relatedCourseIds;
        if (course.duration && course.duration.seconds !== undefined) {
          // eslint-disable-next-line no-unused-vars
          const { seconds, ...durationWithoutSeconds } = course.duration;
          course.duration = durationWithoutSeconds;
        }
      } else {
        course.relatedCourseIds = (coursesIncluded && coursesIncluded[0] && coursesIncluded[0].moduleId) ? [coursesIncluded[0].moduleId] : [];
      }
         // Remove empty string values from duration object
      if (course.duration) {
        const { hours = '', minutes = '', seconds = '' } = course.duration;
        course.duration = {};
        if (hours !== '') course.duration.hours = hours;
        if (minutes !== '') course.duration.minutes = minutes;
        if (course.moduleType === 'VID' && seconds !== '') course.duration.seconds = seconds;
      }
      if (!course.accessResources)  course.accessResources = [];
      dispatch(HANDLE_STATUS('saving'));
      await axios.put(`${SAVE_CONTENT_METADATA_URL}/course`, course);
      dispatch(HANDLE_STATUS('success'));
      setTimeout(() => {
        dispatch(HANDLE_STATUS('idle'));
      }, 3000);}
    } catch (err) {
      dispatch(HANDLE_STATUS('error'));
      dispatch(HANDLE_SERVER_ERR(true));
    }
  };

export const create_new_course = ({ language = 'en_GB' }) =>
  async function (dispatch, getState) {
    try {
      const {
        course: { data, coursesIncluded }
      } = getState();
      // course.accessResources = setAccessResources(course, additionalSubscriptions);
      const createErrMsg = getUpdateFieldsErrMsg(data.updateTitle, data.updateDescription, data.inProgress);
      if (createErrMsg) {
        console.error('[CourseEdit] Validation error:', createErrMsg);
        dispatch(SET_ERR_MSG(createErrMsg));
        dispatch(HANDLE_STATUS('error'));
        dispatch(HANDLE_SERVER_ERR(true));
      }
      else {
      dispatch(HANDLE_STATUS('saving'));
      const coursePayload = { ...data };
      if (coursePayload.moduleType === 'VID') {
        coursePayload.relatedCourseIds = (coursesIncluded && coursesIncluded[0] && coursesIncluded[0].moduleId) ? [coursesIncluded[0].moduleId] : [];
      
      } else {
        delete coursePayload.relatedCourseIds;
        delete coursePayload.overview;
      }

      // Remove empty string values from duration object
      if (coursePayload.duration) {
        const { hours = '', minutes = '', seconds = '' } = coursePayload.duration;
        coursePayload.duration = {};
        if (hours !== '') coursePayload.duration.hours = hours;
        if (minutes !== '') coursePayload.duration.minutes = minutes;
        if (coursePayload.moduleType === 'VID' && seconds !== '') coursePayload.duration.seconds = seconds;
      }
      if (!coursePayload.accessResources)  coursePayload.accessResources = [];
      const response = await axios.post(`${SAVE_CONTENT_METADATA_URL}/course`, coursePayload);
      delete response.data.datePublishedSort;
      delete response.data.dateUpdatedSort;
      if (!response.data.contributors || response.data.contributors.length < 1) response.data.contributors = [];
      dispatch(SET_COURSE(response.data));
      dispatch(HANDLE_STATUS('success'));
      if (data.moduleType === 'LEP') {
        dispatch(save_questions({ id: response.data.id, language })),
        dispatch(save_chapters({ id: response.data.id, language }));
      }
      if (data.moduleType === 'MCQ') {
        dispatch(save_questions({ id: response.data.id, language }));
      }
      setTimeout(() => {
        dispatch(HANDLE_STATUS('idle'));
      }, 3000);
      // replace in the browser address url the word "new" with the id of the newly created course
      window.history.pushState(null, null, `/course/${response.data.id}`);}
    } catch (e) {
      dispatch(HANDLE_STATUS('error'));
      dispatch(HANDLE_SERVER_ERR(true));
    }
  };

  export const fetch_course_included_section = ({ courseId = '', language = 'en-gb' }) =>
  async function (dispatch, getState) {
    const {
        course: { data }
      } = getState();
    try {
      dispatch(HANDLE_COURSE_STATUS({ key: 'isFetching', value: true }));
      const url = `${COURSES_METADATA_URL}?moduleIds=${courseId}&language=${langSwitch(language)}`;
      const { data } = await axios.get(url);
      dispatch(UPDATE_LAST_COURSE_METADATA(data.courses[0]));
      dispatch(HANDLE_COURSE_STATUS({ key: 'isFetching', value: false }));
    } catch (err) {
      dispatch(HANDLE_COURSE_STATUS({ key: 'isFetching', value: false }));
      dispatch(HANDLE_COURSE_STATUS({ key: 'isError', value: true }));
      dispatch(HANDLE_COURSE_STATUS({ key: 'title', value: null }));
      dispatch(HANDLE_COURSE_STATUS({ key: 'duration', value: '' }));
      dispatch(HANDLE_COURSE_STATUS({ key: 'moduleId', value: '' }));
      // eslint-disable-next-line no-unused-vars
      const { relatedCourseId, ...courseData } = data;
      dispatch(SET_COURSE(courseData));
    }

  };

export default courseSlice.reducer;
